import { ZERO_IN_BIG_NUMBER } from '@shared/utils/constant'
import { BigNumber, toBigNumber } from '@injectivelabs/utils/big-number'
import { OrderSide, TradeDirection } from '@injectivelabs/sdk-ts/types/light'
import { calculatePostTradePosition } from '@/app/utils/trading/position'
import { calculateLiquidationPrice } from '@/app/client/utils/derivatives'
import { RfqDirection } from '@/types/rfq'
import type { BffDerivativeMarket } from '@shared/types'
import type { BigNumber as BigNumberType } from '@injectivelabs/utils/big-number'
import type {
  TcDerivativePosition,
  RFQGwPrepareAutoSignResponseType
} from '@injectivelabs/sdk-ts'

const OPTIMISTIC_RFQ_EXECUTION_TTL_MS = 60_000
const OPTIMISTIC_RFQ_PNL_SUPPRESSION_MS = 5_000

type OptimisticRfqGatewayOrder = {
  margin: string
  marketId: string
  quantity: string
  worstPrice: string
  direction: RfqDirection
}

type OptimisticRfqExecution = {
  marketId: string
  expiresAt: number
  subaccountId: string
  position?: TcDerivativePosition
  snapshot: TcDerivativePosition[]
}

export type OptimisticRfqExecutionReference = {
  marketId: string
  subaccountId: string
}

type RfqGatewayMatchedFill = {
  price: BigNumberType
  margin: BigNumberType
  quantity: BigNumberType
}

export type OptimisticRfqExecutionSnapshot = {
  marketId: string
  subaccountId: string
  positions: TcDerivativePosition[]
}

export type OptimisticRfqExecutionMergeOptions = {
  isCompleteSnapshot?: boolean
  confirmedMarketIds?: string[]
}

const POSITION_CONFIRMATION_FIELDS = [
  'margin',
  'quantity',
  'updatedAt',
  'direction',
  'entryPrice',
  'subaccountId'
] as const satisfies ReadonlyArray<keyof TcDerivativePosition>

const optimisticRfqExecutions = new Map<string, OptimisticRfqExecution>()
const optimisticRfqExecutionTimers = new Map<
  string,
  ReturnType<typeof setTimeout>
>()
const optimisticRfqPnlSuppressionTimers = new Map<
  string,
  ReturnType<typeof setTimeout>
>()

export function applyOptimisticRfqExecution({
  order,
  response
}: {
  order: OptimisticRfqGatewayOrder
  response: RFQGwPrepareAutoSignResponseType
}): undefined | OptimisticRfqExecutionSnapshot {
  const accountStore = useAccountStore()
  const executionReference = {
    marketId: order.marketId,
    subaccountId: accountStore.subaccountId
  }

  if (
    optimisticRfqExecutions.has(
      getOptimisticRfqExecutionKey(executionReference)
    )
  ) {
    return undefined
  }

  const positionStore = usePositionStore()

  const snapshot = {
    marketId: order.marketId,
    subaccountId: executionReference.subaccountId,
    positions: [...positionStore.positions]
  }
  const execution = createOptimisticRfqExecution({
    order,
    response,
    snapshot: snapshot.positions
  })

  if (!execution) {
    return undefined
  }

  setOptimisticRfqExecution(execution)
  positionStore.setPositions(
    applyOptimisticRfqExecutionToPositions({
      execution,
      positions: snapshot.positions
    })
  )

  return snapshot
}

export function rollbackOptimisticRfqExecution(
  snapshot: OptimisticRfqExecutionSnapshot
) {
  const positionStore = usePositionStore()

  clearOptimisticRfqExecution(snapshot)

  if (!isActiveOptimisticRfqSubaccount(snapshot.subaccountId)) {
    return
  }

  positionStore.setPositions(
    getRollbackOptimisticPositions({
      snapshot,
      positions: positionStore.positions
    })
  )
}

export function clearOptimisticRfqExecution(
  reference: string | OptimisticRfqExecutionReference
) {
  const executionReference = getOptimisticRfqExecutionReference(reference)
  const executionKey = getOptimisticRfqExecutionKey(executionReference)
  const marketId = executionReference.marketId
  const positionStore = usePositionStore()
  const hasOptimisticPnlSuppression =
    optimisticRfqPnlSuppressionTimers.has(executionKey)

  clearTimeout(optimisticRfqExecutionTimers.get(executionKey))
  clearTimeout(optimisticRfqPnlSuppressionTimers.get(executionKey))
  optimisticRfqExecutions.delete(executionKey)
  optimisticRfqExecutionTimers.delete(executionKey)
  optimisticRfqPnlSuppressionTimers.delete(executionKey)
  syncOptimisticRfqExecutionMarketIds()

  if (
    hasOptimisticPnlSuppression &&
    !hasOptimisticRfqPnlSuppressionForMarket(marketId)
  ) {
    positionStore.suppressedMarketIds.delete(marketId)
  }
}

export function clearOptimisticRfqExecutions() {
  optimisticRfqExecutions.forEach((execution) => {
    clearOptimisticRfqExecution(execution)
  })
}

export function hasOptimisticRfqExecution(
  reference: OptimisticRfqExecutionReference
) {
  return optimisticRfqExecutions.has(getOptimisticRfqExecutionKey(reference))
}

export function mergePositionsWithOptimisticRfqExecutions(
  positions: TcDerivativePosition[],
  options: OptimisticRfqExecutionMergeOptions = {},
  now = Date.now()
) {
  const executions = Array.from(optimisticRfqExecutions.values()).filter(
    (execution) => {
      if (execution.expiresAt <= now) {
        clearOptimisticRfqExecution(execution)

        return false
      }

      if (!isActiveOptimisticRfqSubaccount(execution.subaccountId)) {
        return false
      }

      if (
        isOptimisticRfqExecutionConfirmed({
          options,
          positions,
          execution
        })
      ) {
        clearOptimisticRfqExecution(execution)

        return false
      }

      return true
    }
  )

  if (executions.length === 0) {
    return positions
  }

  return executions.reduce(
    (positionList, execution) =>
      applyOptimisticRfqExecutionToPositions({
        execution,
        positions: positionList
      }),
    positions
  )
}

export function getRfqGatewayMatchedFill({
  order,
  response
}: {
  order: OptimisticRfqGatewayOrder
  response: RFQGwPrepareAutoSignResponseType
}): undefined | RfqGatewayMatchedFill {
  const requestedQuantity = toBigNumber(order.quantity)
  const requestedMargin = toBigNumber(order.margin)
  const quotes = response.quotes || []

  if (requestedQuantity.lte(0) || quotes.length === 0) {
    return undefined
  }

  const matched = quotes.reduce(
    (result, quote) => {
      const quoteQuantity = toBigNumber(quote.quantity)

      if (quoteQuantity.lte(0) || result.remainingQuantity.lte(0)) {
        return result
      }

      const fillQuantity = BigNumber.min(
        quoteQuantity,
        result.remainingQuantity
      )
      const fillRatio = fillQuantity.div(quoteQuantity)

      return {
        margin: result.margin.plus(toBigNumber(quote.margin).times(fillRatio)),
        quantity: result.quantity.plus(fillQuantity),
        remainingQuantity: result.remainingQuantity.minus(fillQuantity),
        weightedPrice: result.weightedPrice.plus(
          toBigNumber(quote.price).times(fillQuantity)
        )
      }
    },
    {
      margin: ZERO_IN_BIG_NUMBER,
      quantity: ZERO_IN_BIG_NUMBER,
      weightedPrice: ZERO_IN_BIG_NUMBER,
      remainingQuantity: requestedQuantity
    }
  )

  if (matched.quantity.lte(0)) {
    return undefined
  }

  const fallbackMargin = requestedMargin.times(
    matched.quantity.div(requestedQuantity)
  )
  const margin = requestedMargin.isZero()
    ? ZERO_IN_BIG_NUMBER
    : matched.margin.gt(0)
      ? matched.margin
      : fallbackMargin

  return {
    margin,
    quantity: matched.quantity,
    price: matched.weightedPrice.div(matched.quantity)
  }
}

function createOptimisticRfqExecution({
  order,
  response,
  snapshot
}: {
  snapshot: TcDerivativePosition[]
  order: OptimisticRfqGatewayOrder
  response: RFQGwPrepareAutoSignResponseType
}): undefined | OptimisticRfqExecution {
  const fill = getRfqGatewayMatchedFill({ order, response })

  if (!fill) {
    return undefined
  }

  const accountStore = useAccountStore()
  const market = useDerivativeStore().marketByIdOrSlug(order.marketId)
  const currentPosition = usePositionStore().subaccountPositions.find(
    (position) => position.marketId === order.marketId
  )

  if (!currentPosition && toBigNumber(order.margin).isZero()) {
    return undefined
  }

  const executionFill = getOptimisticRfqExecutionFill({
    fill,
    order,
    currentPosition
  })

  if (!executionFill) {
    return undefined
  }

  const postTradePosition = calculatePostTradePosition({
    currentPosition,
    order: {
      price: executionFill.price,
      quantity: executionFill.quantity,
      isBuy: order.direction === RfqDirection.Long,
      margin: toBigNumber(order.margin).isZero()
        ? ZERO_IN_BIG_NUMBER
        : executionFill.margin
    }
  })

  const position = createOptimisticPosition({
    order,
    market,
    fill: executionFill,
    currentPosition,
    postTradePosition
  })

  return {
    position,
    snapshot,
    marketId: order.marketId,
    subaccountId: currentPosition?.subaccountId || accountStore.subaccountId,
    expiresAt: Date.now() + OPTIMISTIC_RFQ_EXECUTION_TTL_MS
  }
}

function getOptimisticRfqExecutionFill({
  fill,
  order,
  currentPosition
}: {
  fill: RfqGatewayMatchedFill
  order: OptimisticRfqGatewayOrder
  currentPosition?: TcDerivativePosition
}): undefined | RfqGatewayMatchedFill {
  if (!toBigNumber(order.margin).isZero()) {
    return fill
  }

  const currentQuantity = toBigNumber(currentPosition?.quantity || 0)
  const quantity = BigNumber.min(fill.quantity, currentQuantity)

  if (quantity.lte(0)) {
    return undefined
  }

  return {
    ...fill,
    quantity,
    margin: ZERO_IN_BIG_NUMBER
  }
}

function createOptimisticPosition({
  fill,
  order,
  market,
  currentPosition,
  postTradePosition
}: {
  fill: RfqGatewayMatchedFill
  market?: BffDerivativeMarket
  order: OptimisticRfqGatewayOrder
  currentPosition?: TcDerivativePosition
  postTradePosition: ReturnType<typeof calculatePostTradePosition>
}): undefined | TcDerivativePosition {
  if (postTradePosition.quantity.isZero()) {
    return undefined
  }

  const accountStore = useAccountStore()
  const liquidationPrice = market
    ? calculateLiquidationPrice({
        market,
        price: postTradePosition.entryPrice.toFixed(),
        quantity: postTradePosition.quantity.toFixed(),
        notionalWithLeverage: postTradePosition.margin.toFixed(),
        orderType:
          postTradePosition.direction === TradeDirection.Long
            ? OrderSide.Buy
            : OrderSide.Sell
      })
    : ZERO_IN_BIG_NUMBER

  return {
    upnl: '0',
    fundingSum: '0',
    fundingLast: '0',
    marketId: order.marketId,
    markPrice: fill.price.toFixed(),
    updatedAt: Date.now(),
    direction: postTradePosition.direction,
    margin: postTradePosition.margin.toFixed(),
    entryPrice: postTradePosition.entryPrice.toFixed(),
    quantity: postTradePosition.quantity.toFixed(),
    denom: currentPosition?.denom || market?.quoteToken.denom || '',
    ticker: currentPosition?.ticker || market?.ticker || '',
    liquidationPrice: liquidationPrice.toFixed(),
    subaccountId: currentPosition?.subaccountId || accountStore.subaccountId,
    cumulativeFundingEntry: currentPosition?.cumulativeFundingEntry || '0',
    effectiveCumulativeFundingEntry:
      currentPosition?.effectiveCumulativeFundingEntry || '0'
  }
}

function applyOptimisticRfqExecutionToPositions({
  execution,
  positions
}: {
  execution: OptimisticRfqExecution
  positions: TcDerivativePosition[]
}) {
  const positionList = positions.filter(
    (position) => !isOptimisticExecutionPosition({ execution, position })
  )

  if (!execution.position) {
    return positionList
  }

  return [execution.position, ...positionList]
}

function setOptimisticRfqExecution(execution: OptimisticRfqExecution) {
  const marketId = execution.marketId
  const executionKey = getOptimisticRfqExecutionKey(execution)
  const positionStore = usePositionStore()

  clearOptimisticRfqExecution(execution)
  optimisticRfqExecutions.set(executionKey, execution)
  positionStore.optimisticRfqExecutionMarketIds.add(marketId)
  suppressOptimisticRfqPnl(execution)
  optimisticRfqExecutionTimers.set(
    executionKey,
    setTimeout(() => {
      expireOptimisticRfqExecution(execution)
    }, OPTIMISTIC_RFQ_EXECUTION_TTL_MS)
  )
}

function suppressOptimisticRfqPnl(execution: OptimisticRfqExecution) {
  const marketId = execution.marketId
  const executionKey = getOptimisticRfqExecutionKey(execution)
  const positionStore = usePositionStore()

  positionStore.suppressedMarketIds.add(marketId)
  const timerId = setTimeout(() => {
    if (optimisticRfqPnlSuppressionTimers.get(executionKey) !== timerId) {
      return
    }

    optimisticRfqPnlSuppressionTimers.delete(executionKey)

    if (!hasOptimisticRfqPnlSuppressionForMarket(marketId)) {
      positionStore.suppressedMarketIds.delete(marketId)
    }
  }, OPTIMISTIC_RFQ_PNL_SUPPRESSION_MS)

  optimisticRfqPnlSuppressionTimers.set(executionKey, timerId)
}

function expireOptimisticRfqExecution(execution: OptimisticRfqExecution) {
  const positionStore = usePositionStore()
  const executionKey = getOptimisticRfqExecutionKey(execution)

  if (optimisticRfqExecutions.get(executionKey) !== execution) {
    return
  }

  clearOptimisticRfqExecution(execution)

  if (!isActiveOptimisticRfqSubaccount(execution.subaccountId)) {
    return
  }

  positionStore.setPositions(
    getExpiredOptimisticPositions({
      execution,
      positions: positionStore.positions
    })
  )
}

function getOptimisticRfqExecutionReference(
  reference: string | OptimisticRfqExecutionReference
): OptimisticRfqExecutionReference {
  if (typeof reference !== 'string') {
    return reference
  }

  return {
    marketId: reference,
    subaccountId: useAccountStore().subaccountId
  }
}

function getOptimisticRfqExecutionKey({
  marketId,
  subaccountId
}: OptimisticRfqExecutionReference) {
  return `${subaccountId}:${marketId}`
}

function syncOptimisticRfqExecutionMarketIds() {
  const positionStore = usePositionStore()
  const marketIds = Array.from(optimisticRfqExecutions.values()).map(
    (execution) => execution.marketId
  )

  positionStore.optimisticRfqExecutionMarketIds = new Set(marketIds)
}

function hasOptimisticRfqPnlSuppressionForMarket(marketId: string) {
  return Array.from(optimisticRfqPnlSuppressionTimers.keys()).some((key) =>
    key.endsWith(`:${marketId}`)
  )
}

function isActiveOptimisticRfqSubaccount(subaccountId: string) {
  return useAccountStore().subaccountId === subaccountId
}

function getExpiredOptimisticPositions({
  execution,
  positions
}: {
  execution: OptimisticRfqExecution
  positions: TcDerivativePosition[]
}) {
  const snapshotPosition = execution.snapshot.find((position) =>
    isOptimisticExecutionPosition({ execution, position })
  )
  const currentPositions = positions.filter(
    (position) => !isOptimisticExecutionPosition({ execution, position })
  )

  if (!snapshotPosition) {
    return currentPositions
  }

  return [snapshotPosition, ...currentPositions]
}

function getRollbackOptimisticPositions({
  snapshot,
  positions
}: {
  positions: TcDerivativePosition[]
  snapshot: OptimisticRfqExecutionSnapshot
}) {
  const snapshotPosition = snapshot.positions.find((position) =>
    isOptimisticSnapshotPosition({ snapshot, position })
  )
  const currentPositions = positions.filter(
    (position) => !isOptimisticSnapshotPosition({ snapshot, position })
  )

  if (!snapshotPosition) {
    return currentPositions
  }

  return [snapshotPosition, ...currentPositions]
}

function isOptimisticRfqExecutionConfirmed({
  options,
  positions,
  execution
}: {
  positions: TcDerivativePosition[]
  execution: OptimisticRfqExecution
  options: OptimisticRfqExecutionMergeOptions
}) {
  const confirmedMarketIds = new Set(options.confirmedMarketIds || [])

  if (
    !options.isCompleteSnapshot &&
    !confirmedMarketIds.has(execution.marketId)
  ) {
    return false
  }

  const livePosition = positions.find((position) =>
    isOptimisticExecutionPosition({ execution, position })
  )
  const snapshotPosition = execution.snapshot.find((position) =>
    isOptimisticExecutionPosition({ execution, position })
  )

  if (livePosition) {
    return (
      !snapshotPosition || hasPositionChanged(livePosition, snapshotPosition)
    )
  }

  return !execution.position
}

function isOptimisticExecutionPosition({
  position,
  execution
}: {
  position: TcDerivativePosition
  execution: OptimisticRfqExecution
}) {
  return (
    position.marketId === execution.marketId &&
    position.subaccountId === execution.subaccountId
  )
}

function isOptimisticSnapshotPosition({
  position,
  snapshot
}: {
  position: TcDerivativePosition
  snapshot: OptimisticRfqExecutionSnapshot
}) {
  return (
    position.marketId === snapshot.marketId &&
    position.subaccountId === snapshot.subaccountId
  )
}

function hasPositionChanged(
  position: TcDerivativePosition,
  snapshotPosition: TcDerivativePosition
) {
  return POSITION_CONFIRMATION_FIELDS.some(
    (field) => position[field] !== snapshotPosition[field]
  )
}
